import { toMin } from "../../global/utility/statisticsUtil";
import { CHART_TOOL_BOX } from "./statisticsConstants";

export const CHART_FONT_FAMILY = "Verizon NHG eTX";

// Fixed px, not "%": the dataZoom slider's drag handle is a fixed-size icon, so this
// margin must stay a constant number of pixels regardless of chart width to keep the
// handle fully on-screen — a percentage would shrink below the handle size on narrow charts.
export const CHART_GRID_RIGHT_PADDING = 20;

export const TOOLTIP_STYLE = `font-family:'Verizon NHG eTX';font-size:10px;text-align:left;color:#000000`;

export const chartTextStyle = {
  fontFamily: CHART_FONT_FAMILY,
  fontSize: 12,
};

export const buildChartTitle = (title) =>
  title
    ? {
        text: title,
        left: "center",
        textStyle: {
          fontSize: 14,
          fontWeight: "bold",
        },
      }
    : undefined;

export const buildLegend = (show = true) => ({
  show,
  type: 'scroll',
  top: 0,
  right:200,
  textStyle: {
    fontFamily: CHART_FONT_FAMILY,
    fontSize: 12,
  }
});

const SLIDER_BOTTOM = 5;
const SLIDER_HEIGHT = 30;
const SLIDER_LABEL_GAP = 4;
const SLIDER_LABEL_EDGE = 40;
const SLIDER_LABEL_MIN_SPACING = 90;

// Leaves room under the plot area for the x-axis labels/name plus the slider and its time labels.
export const CHART_GRID_BOTTOM = "20%";

export const buildSliderDataZoom = (axisIndex, axis = "x") => [
  {
    type: "slider",
    ...(axis === "x" ? { xAxisIndex: axisIndex } : { yAxisIndex: axisIndex }),
    show: true,
    bottom: SLIDER_BOTTOM,
    height: SLIDER_HEIGHT,
    // ECharts pins the built-in handle labels to the left/right of each handle and offers no
    // option to move them, so they are turned off and drawn above the slider instead
    // (see SLIDER_LABEL_EVENTS).
    showDetail: false,
    // No explicit left/right/width here: leaving them unset lets ECharts
    // auto-align the slider to the grid's plotting rect (via SliderZoomView's
    // "ph" placeholder resolution), so the slider starts exactly where the
    // x-axis (x=0) starts rather than at a fixed offset that can drift from
    // the axis when different charts use different grid.left/containLabel widths.
  }
];

const clampIndex = (value, length) => Math.min(Math.max(Math.round(value), 0), length - 1);

const categoryText = (item) => String(item?.value ?? item ?? "");

// Handle values can be fractional and, on a boundaryGap axis, land half a step past the last
// category, so they are rounded and clamped before looking up the category.
export const computeSliderLabels = ({
  categories,
  start = 0,
  end = 100,
  startValue,
  endValue,
  rect,
  chartWidth,
  chartHeight,
}) => {
  const n = categories.length;
  const toValue = (value, pct) => (Number.isFinite(value) ? value : -0.5 + (n * pct) / 100);
  const startX = rect.x + (rect.width * start) / 100;
  const endX = rect.x + (rect.width * end) / 100;

  let startAlign = "center";
  let endAlign = "center";
  if (endX - startX < SLIDER_LABEL_MIN_SPACING) {
    startAlign = "right";
    endAlign = "left";
  } else {
    if (startX < SLIDER_LABEL_EDGE) startAlign = "left";
    if (endX > chartWidth - SLIDER_LABEL_EDGE) endAlign = "right";
  }

  const y = chartHeight - SLIDER_BOTTOM - SLIDER_HEIGHT - SLIDER_LABEL_GAP;
  return [
    {
      id: "sliderLabelStart",
      text: categoryText(categories[clampIndex(toValue(startValue, start), n)]),
      x: startX,
      y,
      align: startAlign,
    },
    {
      id: "sliderLabelEnd",
      text: categoryText(categories[clampIndex(toValue(endValue, end), n)]),
      x: endX,
      y,
      align: endAlign,
    },
  ];
};

const toGraphicElement = ({ id, text, x, y, align }) => ({
  id,
  type: "text",
  silent: true,
  z: 100,
  x,
  y,
  style: {
    text,
    align,
    verticalAlign: "bottom",
    fill: "#000000",
    fontFamily: CHART_FONT_FAMILY,
    fontSize: 12,
  },
});

const appliedSignatures = new WeakMap();

// Applying graphic triggers another `finished` event, so skip when nothing changed and the
// elements are still on the chart (a `notMerge` update removes them).
const isUpToDate = (chart, elements) => {
  const current = chart.getOption().graphic?.[0]?.elements ?? [];
  const present = elements.every((el) => current.some((c) => c.id === el.id));
  return present && appliedSignatures.get(chart) === JSON.stringify(elements);
};

export const updateSliderLabels = (_params, chart) => {
  if (!chart) return;
  const option = chart.getOption();
  const slider = option.dataZoom?.find((dz) => dz.type === "slider");
  if (!slider) return;
  const xAxisIndex = Array.isArray(slider.xAxisIndex) ? slider.xAxisIndex[0] : slider.xAxisIndex ?? 0;
  const categories = option.xAxis?.[xAxisIndex]?.data;
  if (!Array.isArray(categories) || categories.length === 0) return;
  const rect = chart.getModel().getComponent("grid", 0)?.coordinateSystem?.getRect();
  if (!rect) return;

  const labels = computeSliderLabels({
    categories,
    start: slider.start,
    end: slider.end,
    startValue: slider.startValue,
    endValue: slider.endValue,
    rect,
    chartWidth: chart.getWidth(),
    chartHeight: chart.getHeight(),
  });
  const elements = labels.map(toGraphicElement);
  if (isUpToDate(chart, elements)) return;
  appliedSignatures.set(chart, JSON.stringify(elements));
  chart.setOption({ graphic: { elements } });
};

// Pass as `onEvents` to <ReactECharts>. Charts use `notMerge`, which wipes the graphic layer on
// every option change, so the labels are re-applied once the chart has finished rendering.
export const SLIDER_LABEL_EVENTS = {
  dataZoom: updateSliderLabels,
  finished: updateSliderLabels,
};

export const formatAxisValue = (val, toolTipUnit) => {
  if (toolTipUnit === " minute(s)") return toMin(val);
  const num = Number(val) || 0;
  if (num >= 1000000) return `${num / 1000000}M`;
  if (num >= 1000) return `${num / 1000}k`;
  return String(num);
};

export const buildYAxisNameGap = (maxVal, toolTipUnit) => {
  const formattedLabel = formatAxisValue(maxVal, toolTipUnit);
  return Math.max(30, formattedLabel.length * 9);
};

const getDataZoomRange = (chart) => {
  const dz = chart.getOption().dataZoom?.[0];
  return { start: dz?.start ?? 0, end: dz?.end ?? 100 };
};

const dispatchZoom = (chart, start, end) =>
  chart.dispatchAction({ type: "dataZoom", dataZoomIndex: 0, start, end });

const makeToolboxButton = (title, icon, onclick) => ({ show: true, title, icon, onclick });

export const createChartToolbox = (chartRef) => {
  const withChart = (fn) => () => {
    const chart = chartRef.current?.getEchartsInstance();
    if (chart) fn(chart);
  };

  return {
    show: true,
    feature: {
      myZoomIn: makeToolboxButton(CHART_TOOL_BOX.ZOOM_IN, CHART_TOOL_BOX.ZOOM_IN_ICON, withChart((chart) => {
        const { start, end } = getDataZoomRange(chart);
        const center = (start + end) / 2;
        const newHalf = ((end - start) / 2) * 0.7;
        dispatchZoom(chart, Math.max(0, center - newHalf), Math.min(100, center + newHalf));
      })),
      myZoomOut: makeToolboxButton(CHART_TOOL_BOX.ZOOM_OUT, CHART_TOOL_BOX.ZOOM_OUT_ICON, withChart((chart) => {
        const { start, end } = getDataZoomRange(chart);
        const center = (start + end) / 2;
        const newHalf = ((end - start) / 2) / 0.7;
        dispatchZoom(chart, Math.max(0, center - newHalf), Math.min(100, center + newHalf));
      })),
      myPanLeft: makeToolboxButton(CHART_TOOL_BOX.SLIDE_LEFT, CHART_TOOL_BOX.SLIDE_LEFT_ICON, withChart((chart) => {
        const { start, end } = getDataZoomRange(chart);
        const windowSize = end - start;
        const newStart = Math.max(start - windowSize * 0.1, 0);
        dispatchZoom(chart, newStart, newStart + windowSize);
      })),
      myPanRight: makeToolboxButton(CHART_TOOL_BOX.SLIDE_RIGHT, CHART_TOOL_BOX.SLIDE_RIGHT_ICON, withChart((chart) => {
        const { start, end } = getDataZoomRange(chart);
        const windowSize = end - start;
        const newEnd = Math.min(end + windowSize * 0.1, 100);
        dispatchZoom(chart, newEnd - windowSize, newEnd);
      })),
      myRestore: makeToolboxButton(CHART_TOOL_BOX.RESET_ZOOM, CHART_TOOL_BOX.RESET_ZOOM_ICON, withChart((chart) => {
        chart.dispatchAction({ type: "restore" });
      })),
    },
    right: 15,
    top: -10,
    itemSize: 18,
  };
};
